// erpnext16_theme entry bundle.

import { theme } from "./core/prefs.js";
import { themePicker } from "./core/theme_picker.js";
import { navbarInject } from "./core/navbar_inject.js";
import { commandPalette } from "./core/command_palette.js";
import { workspacePainter } from "./core/workspace_paint.js";
import { dashboardPolish } from "./core/dashboard_polish.js";
import { workbench } from "./core/workbench.js";
import { bootLoader } from "./core/boot_loader.js";
import { fab } from "./core/fab.js";
import { motion } from "./core/motion.js";
import { shortcutsOverlay } from "./core/shortcuts_overlay.js";
import { formActions } from "./core/form_actions.js";
import { deskFixes } from "./core/desk_fixes.js";
import { uxPrefs } from "./core/ux_prefs.js";
import { accountantTools } from "./core/accountant_tools.js";
import { productivity } from "./core/productivity.js";
import { dateEnhance } from "./core/date_enhance.js";
import { hijriDatepicker } from "./core/hijri_datepicker.js";
import { gridGuard } from "./core/grid_guard.js";
import { pdfExport } from "./core/pdf_export.js";
import { loginBrand } from "./core/login_brand.js";
import { dialogDropdownFix } from "./core/dialog_dropdown_fix.js";
import { realtimeFix } from "./core/realtime_fix.js";
import { reportTotalsFix } from "./core/report_totals_fix.js";
import { currencyFormatter } from "./core/currency_formatter.js";

const NS = (window.e16 = window.e16 || {});
NS.theme = theme;
NS.themePicker = themePicker;
NS.commandPalette = commandPalette;
NS.formActions = formActions;
NS.uxPrefs = uxPrefs;

// ---------------------------------------------------------------------
// SAFE-MODE KILL SWITCH — instant, total, reversible isolation.
//   • URL `?e16off=1`  OR  localStorage 'e16:off' === '1'
//     → disables ALL theme CSS + JS. Pure stock Frappe. Use this to
//       prove in one click whether the theme is the cause of any issue.
//   • Console helpers: e16.off()  /  e16.on()  (persist via localStorage)
// ---------------------------------------------------------------------
function e16IsOff() {
	try {
		if (/[?&]e16off=1\b/.test(location.search)) return true;
		if (/[?&]e16off=0\b/.test(location.search)) { localStorage.removeItem("e16:off"); return false; }
		return localStorage.getItem("e16:off") === "1";
	} catch (e) { return false; }
}

function e16DisableCss() {
	try {
		document
			.querySelectorAll(
				'link[href*="erpnext16_theme"],style[data-e16],link[rel="stylesheet"][href*="erpnext16_theme.bundle"]'
			)
			.forEach((n) => {
				n.setAttribute("media", "not all");
				n.disabled = true;
			});
		document.documentElement.removeAttribute("data-e16-accent");
		document.documentElement.removeAttribute("data-e16-mode");
		document.documentElement.removeAttribute("data-e16-density");
		document.documentElement.className = document.documentElement.className
			.replace(/\be16-[\w-]+/g, "")
			.trim();
	} catch (e) {}
}

NS.off = function () {
	try { localStorage.setItem("e16:off", "1"); } catch (e) {}
	location.reload();
};
NS.on = function () {
	try { localStorage.removeItem("e16:off"); } catch (e) {}
	location.reload();
};

function bootstrap() {
	if (e16IsOff()) {
		e16DisableCss();
		console.warn(
			"%c[e16] SAFE MODE — theme fully disabled (e16.on() to re-enable)",
			"color:#b91c1c;font-weight:700"
		);
		return;
	}

	// 0. Ensure local bench sessions connect to the real socket.io port.
	try { realtimeFix.mount(); } catch (e) { console.warn("[e16] realtime-fix", e); }

	// 1. Apply theme/mode/lang/paint BEFORE first paint where possible.
	try { theme.bootstrap(); } catch (e) { console.warn("[e16] prefs bootstrap", e); }

	// 1a. UX prefs (density / focus / power features) before first paint.
	try { uxPrefs.bootstrap(); } catch (e) { console.warn("[e16] ux-prefs", e); }

	// 1a-bis. Currency symbol patch — mount ASAP so Currency cells render
	//         the styled SAR ﷼ / EGP ج.م glyph from the first list paint.
	try { currencyFormatter.mount(); } catch (e) { console.warn("[e16] currency", e); }

	// 1b. Bulletproof desk fixes (dropdown escape + checkbox dedupe).
	//     Mount ASAP so the observer is live before the first list renders.
	try { deskFixes.mount(); } catch (e) { console.warn("[e16] desk-fixes", e); }

	// 1c. GRID GUARD — patch frappe.dom.freeze ASAP so the Child Table
	//     editor is inline-forced above the freeze the very first time.
	try { gridGuard.mount(); } catch (e) { console.warn("[e16] grid-guard", e); }

	// 2. Boot loader (if enabled).
	try { bootLoader.mount(); } catch (e) { console.warn("[e16] loader", e); }

	// 3. UX additions on DOM ready.
	const ready = () => {
		try { fab.mount(); } catch (e) { console.warn("[e16] fab", e); }
		try { navbarInject.mount(); } catch (e) { console.warn("[e16] navbar", e); }
		try { commandPalette.mount(); } catch (e) { console.warn("[e16] cmdk", e); }
		try { workspacePainter.mount(); } catch (e) { console.warn("[e16] workspace", e); }
		try { dashboardPolish.mount(); } catch (e) { console.warn("[e16] dashboard", e); }
		try { workbench.mount(); } catch (e) { console.warn("[e16] workbench", e); }
		try { motion.mount(); } catch (e) { console.warn("[e16] motion", e); }
		try { shortcutsOverlay.mount(); } catch (e) { console.warn("[e16] shortcuts", e); }
		try { formActions.mount(); } catch (e) { console.warn("[e16] form-actions", e); }
		try { accountantTools.mount(); } catch (e) { console.warn("[e16] accountant", e); }
		try { productivity.mount(); } catch (e) { console.warn("[e16] productivity", e); }
		try { dateEnhance.mount(); } catch (e) { console.warn("[e16] date-enhance", e); }
		try { hijriDatepicker.mount(); } catch (e) { console.warn("[e16] hijri-datepicker", e); }
		try { pdfExport.mount(); } catch (e) { console.warn("[e16] pdf-export", e); }
		try { loginBrand.mount(); } catch (e) { console.warn("[e16] login-brand", e); }
		try { dialogDropdownFix.mount(); } catch (e) { console.warn("[e16] dialog-dropdown-fix", e); }
		try { reportTotalsFix.mount(); } catch (e) { console.warn("[e16] report-totals-fix", e); }
	};

	if (document.readyState === "loading") {
		document.addEventListener("DOMContentLoaded", ready, { once: true });
	} else {
		ready();
	}
}

bootstrap();
